import React from 'react';
import PropTypes from 'prop-types';

//MUI-ICONS
import {DataGrid, esES, gridClasses} from '@mui/x-data-grid';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import CircleIcon from '@mui/icons-material/Circle';

//UTILS
import {visit} from '../proptypes/visit';
import {lighten} from '@mui/material/styles';
import grey from '@mui/material/colors/grey';
import {REASONS_LABELS, VISIT_STATUS_LABELS} from '../../config';
import {getLabelById} from '../../utils/getLabelById';
import {useTranslation} from 'react-i18next';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import {useRouteMatch} from 'react-router-dom';


const tableSx = {
  [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
    outline: 'none',
  },
  [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
    outline: 'none'
  },
  fontSize: '12px',
  boxShadow: 'none',
  borderRadius: 0,
  border: theme => `1.5px solid ${theme.palette.primary.main}`,
  '& .MuiDataGrid-columnHeaders': {
    bgcolor: theme => lighten(theme.palette.primary.main, 0.70),
    borderBottom: theme => `1.5px solid ${theme.palette.primary.main}`
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: 'common.black',
    fontSize: '12px',
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none'
  },
  '&.MuiPaper-root': {
    border: 2,
    zIndex: 10000
  },
  '&.MuiDataGridPanel-root': {
    border: 2,
    zIndex: 10000
  },
  '& .MuiDataGrid-columnHeader:focus': {
    outline: 'none', // Eliminar el borde de foco
  },
  '& .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none', // Eliminar el borde de foco
  }
};

const VisitsTable = ({visitList, isOffline, onVisitDelete, onSelectVisitZone}) => {
  const {t} = useTranslation();

  const match = useRouteMatch();
  const {interventionZoneId} = match.params;

  const renderIdCell = params => <Link
    target='_blank'  
    sx={{width: 50}} 
    href={`${process.env.GESTION_FORESTAL_URL}administrator/visits/${params.value}`}
    rel='noreferrer'
  >
    {params.value}
  </Link>;

  const renderCommunityCell = params =>
    <Tooltip title={params.value ? params.value.name : ''}>
      <Typography sx={{fontSize: 12, textOverflow: 'ellipsis'}}>{params.value ? params.value.name : ''}</Typography>
    </Tooltip>;
  const renderStatusCell = params =>
    <Tooltip title={getLabelById(params.value, VISIT_STATUS_LABELS).toUpperCase()}>
      <CircleIcon sx={{fontSize: 14, color: VISIT_STATUS_LABELS.find(status => status.id === params.value)?.color || undefined}}/>
    </Tooltip>;
  const renderDeleteCell = params => 
    <IconButton onClick={() => onVisitDelete(params.id)}>
      <DeleteIcon disabled={isOffline} sx={{color: isOffline ? grey[300] : grey[700]}}/>
    </IconButton>;
  const renderReasonCell = params => <Typography
    sx={{fontSize: 12, textOverflow: 'ellipsis'}}>{getLabelById(params.value, REASONS_LABELS)}</Typography>;
  const renderVisitorCell = params => <Typography align={params.value ? 'left' : 'center'}
    sx={{fontSize: 12, textOverflow: 'ellipsis', width: '100%'}}>
    {(params.value && params.value.name) || '-'}
  </Typography>;

  const showZoomInIcon = (params) => params.row.community && params.row.community.id && params.row.intervention_zone && !interventionZoneId;
  const renderViewZoneCell = params => showZoomInIcon(params)
    ? <IconButton onClick={() => onSelectVisitZone(params.row.intervention_zone, params.row.community.id)}>
      <ZoomInIcon/>
    </IconButton>
    : <></>;

  const columns = [
    {
      field: 'id',
      headerName: '',
      headerAlign: 'center',
      align: 'center',
      type: 'number',
      width: 1,
      renderCell: renderIdCell
    },
    {
      field: 'community',
      headerName: t('Comunidad').toUpperCase(),
      headerAlign: 'left',
      flex: 1,
      textOverflow: 'ellipsis',
      renderCell: renderCommunityCell
    },
    {
      field: 'visitor',
      headerName: t('Visitador').toUpperCase(),
      flex: 1,
      headerAlign: 'center',
      renderCell: renderVisitorCell
    },
    {
      field: 'status',
      headerName: '',
      headerAlign: 'center',
      renderCell: renderStatusCell,
      align: 'center',
      width: 1
    },
    {
      field: 'visit_reason',
      headerName: t('Motivo').toUpperCase(),
      headerAlign: 'center',
      flex: 1,
      renderCell: renderReasonCell
    },
    {
      field: 'viewZone',
      headerName: '',
      cellClassName: 'viewZone',
      headerAlign: 'right',
      align: 'left',
      width: 30,
      renderCell: renderViewZoneCell
    },
    {
      field: 'actions',
      headerName: '',
      cellClassName: 'actions',
      headerAlign: 'right',
      align: 'left',
      width: 30,
      renderCell: renderDeleteCell
    }
  ];
  
  return <div style={{width: '100%', maxHeight: '75vh', overflow: 'auto', p: 0, scrollbarColor: 'lightgrey transparent'}}>
    {visitList && <DataGrid
      density='compact'
      sx={tableSx}
      rows={visitList}
      columns={columns}
      autoHeight={true}
      disableColumnMenu={true}
      disableColumnFilter={true}
      disableColumnResize={true}
      disableColumnSorting={true}
      localeText={{
        ...esES.components.MuiDataGrid.defaultProps.localeText,
        noRowsLabel: t('Sin Visitas'),
      }}
      hideFooter
    />}
  </div>;
};

VisitsTable.propTypes = {
  visitList: PropTypes.arrayOf(visit),
  onVisitDelete: PropTypes.func.isRequired,
  onSelectVisitZone: PropTypes.func,
  isOffline: PropTypes.bool.isRequired
};

export default VisitsTable;